/**
 * ResetPasswordComponent - Password Reset for Email Users
 *
 * This component allows users who:
 * - Have email/password authentication
 * - Forgot their password
 *
 * To reset their password via email:
 * 1. User enters their email
 * 2. System sends a password reset email
 * 3. User clicks link in email to set new password
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { safeInternalReturnUrl } from '../../../core/utils/return-url.util';
import { Auth, sendPasswordResetEmail } from '@angular/fire/auth';

type ResetStep = 'email_input' | 'email_sent';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, SharedModule]
})
export class ResetPasswordComponent implements OnInit {
  emailForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  currentStep: ResetStep = 'email_input';
  submittedEmail = '';

  private returnUrl: string = '/auth';
  /**
   * #2009 (G8) — espace de la porte qui a ouvert cette page. La porte du
   * personnel passe `?space=staff` : le lien reçu par e-mail et le bouton de
   * retour ramènent alors à `/backoffice-login`, plus à la porte client.
   */
  private space: 'client' | 'staff' = 'client';
  /** Couture de test : `sendPasswordResetEmail` est une fonction de module, non espionnable. */
  private resetSender = sendPasswordResetEmail;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private auth: Auth
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.captureQueryParams();
  }

  private initializeForm(): void {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  private captureQueryParams(): void {
    this.returnUrl = safeInternalReturnUrl(this.route.snapshot.queryParams['returnUrl'], '/auth');
    this.space = this.route.snapshot.queryParams['space'] === 'staff' ? 'staff' : 'client';
  }

  /** Porte de connexion de l'espace d'où l'on vient. */
  private get loginPath(): string {
    return this.space === 'staff' ? '/backoffice-login' : '/auth';
  }

  /**
   * Submit password reset request
   */
  async onSubmit(): Promise<void> {
    if (this.emailForm.invalid || this.isLoading) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const email = this.emailForm.get('email')?.value.trim().toLowerCase();
    this.submittedEmail = email;

    try {
      // Configure action code settings
      const actionCodeSettings = {
        url: `${window.location.origin}${this.loginPath}`,
        handleCodeInApp: false
      };

      // Send password reset email
      await this.resetSender(this.auth, email, actionCodeSettings);

      console.log('✅ [ResetPassword] Password reset email sent to:', email);

      this.currentStep = 'email_sent';
      this.successMessage = 'AUTH.RESET_PASSWORD.SUCCESS.EMAIL_SENT';

    } catch (error: any) {
      console.error('❌ [ResetPassword] Error sending reset email:', error);

      // Handle specific Firebase errors
      if (error.code === 'auth/user-not-found') {
        this.errorMessage = 'AUTH.RESET_PASSWORD.ERRORS.USER_NOT_FOUND';
      } else if (error.code === 'auth/invalid-email') {
        this.errorMessage = 'AUTH.RESET_PASSWORD.ERRORS.INVALID_EMAIL';
      } else if (error.code === 'auth/too-many-requests') {
        this.errorMessage = 'AUTH.RESET_PASSWORD.ERRORS.TOO_MANY_REQUESTS';
      } else {
        this.errorMessage = 'AUTH.RESET_PASSWORD.ERRORS.GENERIC';
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Resend password reset email
   */
  async onResendEmail(): Promise<void> {
    if (this.isLoading || !this.submittedEmail) return;

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const actionCodeSettings = {
        url: `${window.location.origin}${this.loginPath}`,
        handleCodeInApp: false
      };

      await this.resetSender(this.auth, this.submittedEmail, actionCodeSettings);
      this.successMessage = 'AUTH.RESET_PASSWORD.SUCCESS.EMAIL_RESENT';
    } catch (error) {
      this.errorMessage = 'AUTH.RESET_PASSWORD.ERRORS.RESEND_FAILED';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Go back to email input step
   */
  onBackToEmail(): void {
    this.currentStep = 'email_input';
    this.errorMessage = '';
    this.successMessage = '';
  }

  /**
   * Navigate to login page
   */
  onGoToLogin(): void {
    // #2009 (G8) — le personnel revient à SA porte, sans page d'origine : la
    // porte du personnel mène à l'accueil du back-office après le code.
    if (this.space === 'staff') {
      this.router.navigate(['/backoffice-login']);
      return;
    }
    this.router.navigate(['/auth'], {
      queryParams: { returnUrl: this.returnUrl }
    });
  }

  // Template helpers
  get emailControl() {
    return this.emailForm.get('email');
  }

  get isEmailStep(): boolean {
    return this.currentStep === 'email_input';
  }

  get isEmailSentStep(): boolean {
    return this.currentStep === 'email_sent';
  }
}
